import { SpaceKind, TreeSpaceKind } from 'shared';

import {
  canAccessCompanySettings,
  companySettingsTabFromPathname,
  isCompanySettingsTabVisible,
} from '../pages/company-settings/company-settings-access.util';
import { PageRoute } from '../types/page.types';
import { AccessRole, StakeholderRole } from '../types/stakeholder.types';

export type PageAccessCompany = {
  isDemo?: boolean;
  /** Narrows what the role means: a member who reaches only the data rooms shared with them. */
  isExternalGuest?: boolean;
  roleInCompany: StakeholderRole;
  stakeholder?: {
    accessRole?: AccessRole | null;
    capTableAccess?: boolean;
    dataRoomsAccess?: boolean;
    internalWorkspacesAccess?: boolean;
  };
};

const MANAGERS: StakeholderRole[] = [StakeholderRole.ADMIN, StakeholderRole.CONTRIBUTOR];
const ALL_MEMBERS: StakeholderRole[] = [
  StakeholderRole.ADMIN,
  StakeholderRole.CONTRIBUTOR,
  StakeholderRole.STAKEHOLDER,
];

const DOCUMENT_PATH_PREFIXES: PageRoute[] = [
  PageRoute.DOCUMENTS,
  PageRoute.DOCUMENTS_TEMPLATES,
  PageRoute.DOCUMENTS_SIGNATURES,
];

type ModuleAccessPolicy = {
  allowedPaths?: (company: PageAccessCompany) => readonly string[];
  canAccess?: (company: PageAccessCompany, pathname: string) => boolean;
  deniedRedirect?: string;
  inRoleSidebar?: boolean;
  module: PageRoute;
  pathPrefixes?: readonly string[];
  roles: readonly StakeholderRole[];
};

/**
 * Whether this person runs the company or merely holds something in it. The Data Room page reads
 * it to choose between the owner's view and the recipient's — the same split the server draws with
 * the WORKSPACES matrix, kept here so the page does not have to ask and be refused.
 */
/**
 * The activity centre is company-wide, like any module: 20.2.4.3 §5 gives an External Guest the
 * data rooms shared with them and nothing else, so its counters are not theirs to ask for.
 */
export const canSeeActivityCenter = (company: PageAccessCompany): boolean =>
  !company.isExternalGuest;

export const isCompanyManager = (company: PageAccessCompany): boolean =>
  !company.isExternalGuest && MANAGERS.includes(company.roleInCompany);

/**
 * How much of the Vault the person reaches, as the server's `STAKEHOLDER_VAULT` module draws it:
 * the team reads all of it, a Stakeholder reads the folders that are theirs and changes nothing,
 * and an External Guest has no Vault at all.
 */
export enum VaultAccess {
  FULL = 'full',
  NONE = 'none',
  OWN_FOLDERS = 'own-folders',
}

export const getVaultAccess = (company: PageAccessCompany): VaultAccess => {
  if (company.isExternalGuest) return VaultAccess.NONE;

  return company.roleInCompany === StakeholderRole.STAKEHOLDER
    ? VaultAccess.OWN_FOLDERS
    : VaultAccess.FULL;
};

const SPACE_KIND_TOGGLES: Record<TreeSpaceKind, 'dataRoomsAccess' | 'internalWorkspacesAccess'> = {
  [SpaceKind.DATA_ROOM]: 'dataRoomsAccess',
  [SpaceKind.WORKSPACE]: 'internalWorkspacesAccess',
};

const SPACE_KIND_ORDER: TreeSpaceKind[] = [SpaceKind.WORKSPACE, SpaceKind.DATA_ROOM];

/**
 * The kinds of space the person reaches on the owner's side of the Data Room, in the order Home
 * lists them.
 *
 * The server's `assertWorkspacesSpaceAccess` drawn from the session: an Admin or Contributor gets
 * both, a Guest User (a row with an `accessRole`, Auditor and Contributor alike) only the kinds
 * whose 20.2.4.3 §4 toggle is on, and anybody who is not a manager none. Their toggles mean nothing
 * on anyone else's row, so they are read only for a Guest User.
 */
export const getReachableSpaceKinds = (company: PageAccessCompany): TreeSpaceKind[] => {
  if (!isCompanyManager(company)) return [];

  const guest = company.stakeholder?.accessRole ? company.stakeholder : undefined;

  return SPACE_KIND_ORDER.filter((kind) => !guest || Boolean(guest[SPACE_KIND_TOGGLES[kind]]));
};

/**
 * The kinds of space Home's + Create may offer: the ones reached, unless the person is a Guest
 * Auditor — 7. Create Action gives + Create to a Guest Contributor only, and the server's create
 * routes refuse an auditor whatever their toggles say.
 */
export const getCreatableSpaceKinds = (company: PageAccessCompany): TreeSpaceKind[] =>
  company.stakeholder?.accessRole === AccessRole.AUDITOR ? [] : getReachableSpaceKinds(company);

const pathMatches = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

/**
 * An External Guest carries the STAKEHOLDER role for company membership alone, and 20.2.4.3 §5
 * gives them the data rooms shared with them and nothing else in the company. Their reach is
 * narrowed here rather than by a role of their own, which would have to be taught to every policy
 * below — the same shape the server uses in `assertStakeholderModuleAccess`.
 */
const EXTERNAL_GUEST_MODULES: readonly PageRoute[] = [PageRoute.DATA_ROOM];

export const getRoleHomeRoute = (company: PageAccessCompany): PageRoute => {
  // My Holdings does not exist for a guest, so sending them there is a loop.
  if (company.isExternalGuest) return PageRoute.DATA_ROOM;

  return company.roleInCompany === StakeholderRole.STAKEHOLDER
    ? PageRoute.MY_HOLDINGS
    : PageRoute.DASHBOARD;
};

export const getDocumentSubMenuPaths = (role?: StakeholderRole): PageRoute[] =>
  role === StakeholderRole.STAKEHOLDER
    ? [PageRoute.DOCUMENTS_SIGNATURES]
    : [PageRoute.DOCUMENTS_TEMPLATES, PageRoute.DOCUMENTS_SIGNATURES];

const canAccessCompanySettingsPath = (company: PageAccessCompany, pathname: string): boolean => {
  if (company.isDemo) return false;
  if (!canAccessCompanySettings(company)) return false;
  const tab = companySettingsTabFromPathname(pathname);
  if (pathname !== PageRoute.COMPANY_SETTINGS && tab === null) return false;
  return !tab || isCompanySettingsTabVisible(company, tab);
};

const MODULE_ACCESS: ModuleAccessPolicy[] = [
  { module: PageRoute.DASHBOARD, roles: MANAGERS },
  { module: PageRoute.STAKEHOLDERS, roles: MANAGERS },
  { module: PageRoute.SHARE_CLASSES, roles: MANAGERS },
  { module: PageRoute.POOL_PLANS, roles: MANAGERS },
  { module: PageRoute.EVENTS, roles: MANAGERS },
  { module: PageRoute.SCENARIO_MODELING, roles: MANAGERS },
  {
    module: PageRoute.CAP_TABLE,
    roles: ALL_MEMBERS,
    canAccess: (company) => Boolean(company.stakeholder?.capTableAccess),
  },
  { module: PageRoute.SAFES, roles: MANAGERS },
  { module: PageRoute.MY_HOLDINGS, roles: ALL_MEMBERS },
  { module: PageRoute.DIVIDENDS, roles: MANAGERS },
  {
    module: PageRoute.DOCUMENTS,
    roles: ALL_MEMBERS,
    pathPrefixes: DOCUMENT_PATH_PREFIXES,
    canAccess: (company) => !company.isDemo,
    allowedPaths: (company) =>
      company.roleInCompany === StakeholderRole.STAKEHOLDER
        ? getDocumentSubMenuPaths(company.roleInCompany)
        : DOCUMENT_PATH_PREFIXES,
    deniedRedirect: PageRoute.DOCUMENTS_SIGNATURES,
  },
  {
    // 20.2.4.3 §4 gates Internal Workspaces and Data Rooms per Guest User, but not the module:
    // 20.2 §1 leaves them the Vault whatever the toggles say, and the Vault is inside it. What the
    // page holds is the server's answer, which narrows the feed to the spaces the caller reaches.
    module: PageRoute.DATA_ROOM,
    roles: ALL_MEMBERS,
    pathPrefixes: [
      PageRoute.DATA_ROOM,
      PageRoute.DATA_ROOM_ACTIVITY,
      PageRoute.DATA_ROOM_ACTIVITY_DATA_ROOMS,
      PageRoute.DATA_ROOM_ACTIVITY_FILES,
      PageRoute.DATA_ROOM_ACTIVITY_VISITORS,
      PageRoute.DATA_ROOM_WORKSPACES,
    ],
  },
  { module: PageRoute.VAULT, roles: ALL_MEMBERS },
  {
    module: PageRoute.COMPANY_SETTINGS,
    roles: MANAGERS,
    inRoleSidebar: false,
    canAccess: canAccessCompanySettingsPath,
  },
];

const STAKEHOLDER_SIDEBAR_ORDER: readonly PageRoute[] = [
  PageRoute.MY_HOLDINGS,
  PageRoute.DOCUMENTS,
  PageRoute.DATA_ROOM,
  PageRoute.CAP_TABLE,
  PageRoute.VAULT,
];

const sidebarRank = (link: PageRoute, order: readonly PageRoute[]): number => {
  const rank = order.indexOf(link);
  return rank === -1 ? order.length : rank;
};

const sidebarModulesFor = (role: StakeholderRole): PageRoute[] => {
  const modules = MODULE_ACCESS.filter(
    (policy) => policy.inRoleSidebar !== false && policy.roles.includes(role),
  ).map((policy) => policy.module);

  if (role !== StakeholderRole.STAKEHOLDER) {
    return modules;
  }

  return [...modules].sort(
    (left, right) =>
      sidebarRank(left, STAKEHOLDER_SIDEBAR_ORDER) - sidebarRank(right, STAKEHOLDER_SIDEBAR_ORDER),
  );
};

export const roleModuleLinks: Record<StakeholderRole, readonly PageRoute[]> = {
  [StakeholderRole.ADMIN]: sidebarModulesFor(StakeholderRole.ADMIN),
  [StakeholderRole.CONTRIBUTOR]: sidebarModulesFor(StakeholderRole.CONTRIBUTOR),
  [StakeholderRole.STAKEHOLDER]: sidebarModulesFor(StakeholderRole.STAKEHOLDER),
};

const policyPathPrefixes = (policy: ModuleAccessPolicy): readonly string[] =>
  policy.pathPrefixes ?? [policy.module];

const findPolicy = (pathname: string): ModuleAccessPolicy | undefined =>
  MODULE_ACCESS.find((policy) =>
    policyPathPrefixes(policy).some((prefix) => pathMatches(pathname, prefix)),
  );

const isModuleAllowed = (
  policy: ModuleAccessPolicy,
  company: PageAccessCompany,
  pathname: string,
): boolean =>
  (company.isExternalGuest
    ? EXTERNAL_GUEST_MODULES.includes(policy.module)
    : policy.roles.includes(company.roleInCompany)) &&
  (!policy.canAccess || policy.canAccess(company, pathname));

export const isModuleLinkVisible = (link: PageRoute, company: PageAccessCompany): boolean => {
  const policy = MODULE_ACCESS.find((item) => item.module === link);
  return Boolean(policy && isModuleAllowed(policy, company, link));
};

export const getUnauthorizedRedirect = (
  pathname: string,
  company: PageAccessCompany,
): null | string => {
  const policy = findPolicy(pathname);
  if (!policy) return null;
  if (!isModuleAllowed(policy, company, pathname)) {
    return getRoleHomeRoute(company);
  }
  if (
    policy.allowedPaths &&
    !policy.allowedPaths(company).some((prefix) => pathMatches(pathname, prefix))
  ) {
    return policy.deniedRedirect ?? getRoleHomeRoute(company);
  }
  return null;
};
